// ============================================================
// WEAPON OVERRIDES - Pure Slot → Slot transform for per-attack
// weapon modifications: ±X d10 damage, ±X penetration, damage
// type swap, and special-rule add/upgrade. Applied both at
// attack execution and to the probability inputs so the plots
// always show the weapon the attack will actually use.
// Zero React dependency.
// ============================================================
import { parseDiceExpression } from './dice';
import { parseWS, fmtWS } from './weaponSpecials';
import type { DiceGroup, Slot, WeaponOverrides } from '../types';

export const EMPTY_WEAPON_OVERRIDES: WeaponOverrides = {
  damageDice: 0,
  damageFlat: 0,
  penDelta: 0,
  damageTypeOverride: null,
  specials: [],
};

export function hasActiveOverrides(o: WeaponOverrides | null | undefined): boolean {
  if (!o) return false;
  // damageFlat is optional: pre-v6.25 macro snapshots lack it
  const numbersMoved = o.damageDice !== 0 || (o.damageFlat || 0) !== 0 || o.penDelta !== 0;
  const ruled = !!o.looks?.length || !!o.hitRules?.length;
  return numbersMoved || o.damageTypeOverride != null || o.specials.length > 0 || ruled;
}

function groupToString(g: DiceGroup): string {
  if (g.type === 'nested') return `[${g.countDice}d${g.countSides}]d${g.sides}`;
  if (g.type === 'dice') return `${g.count}d${g.sides}`;
  return String(g.value);
}

function groupsToExpression(groups: DiceGroup[]): string {
  return groups.reduce((expr, g) => {
    const part = groupToString(g);
    if (!expr) return part;
    return part.startsWith('-') ? expr + part : expr + '+' + part;
  }, '');
}

/**
 * Add or remove plain d10s from a damage expression. Only plain d10
 * groups are adjusted (never nested `[1d5]d10` groups); the count
 * clamps at zero (dropping the group). With no d10 group present, a
 * positive delta prepends one.
 */
export function adjustDamageExpression(expr: string, deltaD10: number): string {
  if (deltaD10 === 0) return expr;
  const groups = parseDiceExpression(expr);
  const idx = groups.findIndex((g) => g.type === 'dice' && g.sides === 10);
  if (idx === -1) {
    return deltaD10 <= 0 ? expr : groupsToExpression([{ type: 'dice', count: deltaD10, sides: 10 }, ...groups]);
  }
  const newCount = Math.max(0, (groups[idx].count || 0) + deltaD10);
  return groupsToExpression(replaceOrDrop(groups, idx, newCount === 0 ? null : { ...groups[idx], count: newCount }));
}

/** The groups with the one at `idx` replaced, or dropped when `next` is null. */
function replaceOrDrop(groups: DiceGroup[], idx: number, next: DiceGroup | null): DiceGroup[] {
  const out = groups.slice();
  if (next === null) out.splice(idx, 1);
  else out[idx] = next;
  return out;
}

/**
 * Add a flat delta to a damage expression's first flat modifier (which
 * may go negative - the parser supports "2d10-3"). A group landing on
 * exactly zero is dropped; with no flat group present, one is appended.
 */
export function adjustDamageFlat(expr: string, delta: number): string {
  if (delta === 0) return expr;
  const groups = parseDiceExpression(expr);
  const idx = groups.findIndex((g) => g.type === 'flat');
  if (idx === -1) return groupsToExpression([...groups, { type: 'flat', value: delta }]);
  const newValue = (groups[idx].value || 0) + delta;
  return groupsToExpression(replaceOrDrop(groups, idx, newValue === 0 ? null : { ...groups[idx], value: newValue }));
}

/** Merge added specials into a base list: same-id entries are replaced (parameter upgrade), new ids appended. */
export function mergeSpecials(base: string[], added: string[]): string[] {
  const merged = base.slice();
  for (const sp of added) {
    const { id } = parseWS(sp);
    const existing = merged.findIndex((b) => parseWS(b).id === id);
    if (existing >= 0) merged[existing] = sp;
    else merged.push(sp);
  }
  return merged;
}

/**
 * Apply overrides to a slot, returning a new slot. Returns the SAME
 * reference when no override is active - callers memoize on identity
 * (useProbability deps, memoized components).
 */
export function applyWeaponOverrides(slot: Slot, o: WeaponOverrides | null | undefined): Slot {
  if (!hasActiveOverrides(o) || !o) return slot;
  return {
    ...slot,
    damageExpression: adjustDamageFlat(adjustDamageExpression(slot.damageExpression, o.damageDice), o.damageFlat || 0),
    penetration: Math.max(0, slot.penetration + o.penDelta),
    damageType: o.damageTypeOverride ?? slot.damageType,
    specialRules: mergeSpecials(slot.specialRules || [], o.specials),
    ...(o.looks?.length ? { looks: o.looks } : {}),
    ...(o.hitRules?.length ? { hitRules: o.hitRules } : {}),
  };
}

const fmtSigned = (n: number): string => (n >= 0 ? '+' + n : String(n));

/** Human-readable labels for each active override axis (for skill/damage breakdowns and narrative notes). */
export function describeOverrides(slot: Slot, o: WeaponOverrides | null | undefined): string[] {
  if (!hasActiveOverrides(o) || !o) return [];
  const typeSwapped = o.damageTypeOverride != null && o.damageTypeOverride !== slot.damageType;
  const axes: (string | false)[] = [
    o.damageDice !== 0 && `Override: ${fmtSigned(o.damageDice)}d10 damage`,
    (o.damageFlat || 0) !== 0 && `Override: ${fmtSigned(o.damageFlat!)} damage`,
    o.penDelta !== 0 && `Override: Pen ${fmtSigned(o.penDelta)}`,
    typeSwapped && `Override: damage type ${slot.damageType}→${o.damageTypeOverride}`,
  ];
  const specials = o.specials.map((sp) => specialNote(slot.specialRules || [], sp));
  return [...axes, ...specials].filter((n): n is string => !!n);
}

/** An added special's note: an upgrade names both, a new one gets a +, a repeat says nothing. */
function specialNote(base: string[], sp: string): string | null {
  const { id } = parseWS(sp);
  const existing = base.find((b) => parseWS(b).id === id);
  if (!existing) return `Override: +${fmtWS(sp)}`;
  return existing !== sp ? `Override: ${fmtWS(existing)}→${fmtWS(sp)}` : null;
}
